import React from 'react';
import { Platform, View, Text, StyleSheet, Pressable } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { typography } from '@/styles';

export interface DataTableProps {
	columns?: any[];
	data?: any[];
	onSort?: (column: any, sortDirection: 'asc' | 'desc') => void;
	sortServer?: boolean;
	defaultSortFieldId?: string;
	defaultSortAsc?: boolean;
	pagination?: boolean;
	paginationServer?: boolean;
	paginationTotalRows?: number;
	paginationPerPage?: number;
	/** Initial page (1-based) on mount. RDT keeps page state internally after that. */
	paginationDefaultPage?: number;
	paginationRowsPerPageOptions?: number[];
	onChangePage?: (page: number) => void;
	onChangeRowsPerPage?: (currentRowsPerPage: number, currentPage: number) => void;
	expandableRows?: boolean;
	expandableRowsComponent?: React.ComponentType<{ data: any }>;
	expandableRowDisabled?: (row: any) => boolean;
	expandOnRowClicked?: boolean;
	progressPending?: boolean;
	noDataComponent?: React.ReactNode;
	striped?: boolean;
	conditionalRowStyles?: { when: (row: any) => boolean; style: any }[];
	highlightOnHover?: boolean;
	pointerOnHover?: boolean;
	onRowClicked?: (row: any, event: any) => void;
	selectableRows?: boolean;
	onSelectedRowsChange?: (state: { allSelected: boolean; selectedCount: number; selectedRows: any[] }) => void;
	clearSelectedRows?: boolean;
	/** Pin the header row while the body scrolls (RDT `fixedHeader`). Pair with
	 *  `fixedHeaderScrollHeight` so RDT owns the scroll region. */
	fixedHeader?: boolean;
	fixedHeaderScrollHeight?: string;
	/** Predicate run once per row on mount (and whenever `clearSelectedRows` flips) to seed initial selection. */
	selectableRowSelected?: (row: any) => boolean;
	/** Predicate that disables the selection checkbox for rows that shouldn't be toggleable. */
	selectableRowDisabled?: (row: any) => boolean;
	style?: any;
	className?: string;
	children?: React.ReactNode;
	customStyles?: any;
}

export interface DataTableHeaderProps {
	children: React.ReactNode;
}

export interface DataTableTitleProps {
	children: React.ReactNode;
	numeric?: boolean;
	sortDirection?: 'ascending' | 'descending';
	onPress?: () => void;
	style?: any;
}

export interface DataTableRowProps {
	children: React.ReactNode;
	style?: any;
}

export interface DataTableCellProps {
	children: React.ReactNode;
	numeric?: boolean;
	style?: any;
}

// Lazy import at module scope. If we put `React.lazy` inside the component
// it returns a fresh component type on every render, which makes React
// unmount/remount the entire table on every parent re-render — visible as
// a flash of the "Loading table..." fallback whenever any cell-level state
// changes (e.g. selection checkboxes).
//
// RDT is wrapped in a `StyleSheetManager` whose `shouldForwardProp` stops RDT's
// style-only props (center/right/grow/button/…) from leaking to the DOM under
// styled-components v6 + React 19 (see rdt-forward-prop.ts). styled-components is
// pulled in here (web-only, lazily) so it never lands in the native bundle.
const LazyRDT = React.lazy(async () => {
	const [{ default: RDT }, { StyleSheetManager }, { rdtShouldForwardProp }] = await Promise.all([
		import('react-data-table-component'),
		import('styled-components'),
		import('./rdt-forward-prop'),
	]);
	const Wrapped: React.FC<any> = (props) => (
		<StyleSheetManager shouldForwardProp={rdtShouldForwardProp}>
			<RDT {...props} />
		</StyleSheetManager>
	);
	return { default: Wrapped };
});

// Web DataTable component (only loads on web)
const WebDataTable: React.FC<DataTableProps> = (props) => {
	const theme = useTheme();

	const customStyles = {
		table: {
			style: {
				backgroundColor: theme.colors.surface,
				borderRadius: 8,
				overflow: 'hidden',
			},
		},
		headRow: {
			style: {
				backgroundColor: theme.colors.surfaceVariant,
				borderBottomWidth: 1,
				borderBottomColor: theme.colors.outline,
			},
		},
		headCells: {
			style: {
				fontSize: 12,
				fontWeight: '600',
				color: theme.colors.onSurface,
				paddingLeft: 8,
				paddingRight: 8,
				paddingTop: 12,
				paddingBottom: 12,
			},
		},
		cells: {
			style: {
				fontSize: 12,
				fontFamily: typography.fontFamily,
				color: theme.colors.onSurface,
				paddingLeft: 8,
				paddingRight: 8,
				paddingTop: 8,
				paddingBottom: 8,
			},
		},
		rows: {
			style: {
				borderBottomWidth: 1,
				borderBottomColor: theme.colors.outline,
				'&:hover': {
					backgroundColor: theme.colors.surfaceVariant,
				},
			},
			// Odd rows get a VERY subtle grey so alternating rows are distinguishable
			// without looking banded. (Previously this matched the base surface, so
			// `striped` was a silent no-op.)
			stripedStyle: {
				backgroundColor: '#fafafa',
			},
		},
		pagination: {
			style: {
				backgroundColor: theme.colors.surface,
				borderTopWidth: 1,
				borderTopColor: theme.colors.outline,
				paddingTop: 16,
				paddingBottom: 16,
			},
		},
	};

	// Ensure columns and data are provided for web DataTable
	const webProps = {
		...props,
		columns: props.columns || [],
		data: props.data || [],
	};

	// Use customStyles from props if provided, otherwise use default theme styles
	const finalCustomStyles = props.customStyles || customStyles;

	return (
		<React.Suspense fallback={<View style={styles.loadingContainer}><Text>Loading table...</Text></View>}>
			<LazyRDT
				{...webProps}
				customStyles={finalCustomStyles}
				theme="light"
			/>
		</React.Suspense>
	);
};

const getParamValue = (column: any, row: any) => {
	if (column.cell) {
		return column.cell(row);
	}

	if (column.selector) {
		return column.selector(row);
	}

	if (column.id) {
		return row[column.id];
	}

	if (column.name) {
		return column.name;
	}

	return null;
};

// Mobile DataTable component - shows a message that tables are not supported on mobile
const MobileDataTable: React.FC<DataTableProps> = (props) => {
	const { children, columns, data } = props;

	// If using compound component pattern (children), render children directly
	if (children) {
		return (
			<View style={styles.mobileContainer}>
				{children}
			</View>
		);
	}

	// Simple tables for Mobile
	return (
		<View style={styles.mobileContainer}>
			{/* header */}
			{/*
			<View style={{
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: 16,
				borderBottomWidth: 1,
				borderBottomColor: '#e0e0e0',
				backgroundColor: '#f0f0f0',
			}}>
				{columns?.filter((column) => !column.hideOnMobile).map((column, index) => (
					<Text key={column.id || index}>{column.name}</Text>
				))}
			</View>
			*/}
			{/* rows */}
			{data?.map((row, index) => (
				<Pressable key={row.id || index} onPress={() => props.onRowClicked?.(row, index)}>
					<View key={row.id || index} style={{
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: 16,
						borderBottomWidth: 1,
						borderBottomColor: '#e0e0e0',
					}}>
						{columns?.filter((column) => !column.hideOnMobile).map((column, cid) => (
							<Text key={column.id || cid}>
								{getParamValue(column, row)}
							</Text>
						))}
					</View>
				</Pressable>
			))}
		</View>
	);
};

// Sub-components for compound component pattern
const DataTableHeader: React.FC<DataTableHeaderProps> = ({ children }) => (
	<View style={styles.mobileHeader}>
		{children}
	</View>
);

const DataTableTitle: React.FC<DataTableTitleProps> = ({ children, numeric, sortDirection, onPress, style }) => (
	<Text
		style={[
			styles.mobileTitle,
			numeric && styles.numericText,
			style,
		]}
		onPress={onPress}
	>
		{children}
		{sortDirection && (
			<Text style={styles.sortIndicator}>
				{sortDirection === 'ascending' ? ' ↑' : ' ↓'}
			</Text>
		)}
	</Text>
);

const DataTableRow: React.FC<DataTableRowProps> = ({ children, style }) => (
	<View style={[styles.mobileTableRow, style]}>
		{children}
	</View>
);

const DataTableCell: React.FC<DataTableCellProps> = ({ children, numeric, style }) => (
	<View style={[styles.mobileTableCell, style]}>
		<Text style={[styles.mobileTableCellText, numeric && styles.numericText]}>{children}</Text>
	</View>
);

// Main DataTable component with proper typing
const CustomDataTable = (props: DataTableProps) => {
	// Only render the web DataTable on web platform
	if (Platform.OS === 'web') {
		return <WebDataTable {...props} />;
	}

	// For mobile platforms, render mobile version
	return <MobileDataTable {...props} />;
};

// Create a properly typed DataTable with sub-components
const DataTable = Object.assign(CustomDataTable, {
	Header: DataTableHeader,
	Title: DataTableTitle,
	Row: DataTableRow,
	Cell: DataTableCell,
});

const styles = StyleSheet.create({
	mobileContainer: {
		flex: 1,
		backgroundColor: '#f7f7f7',
	},
	loadingContainer: {
		padding: 20,
		alignItems: 'center',
	},
	loadingText: {
		fontSize: 16,
		color: '#666',
	},
	unsupportedContainer: {
		padding: 20,
		alignItems: 'center',
		justifyContent: 'center',
		flex: 1,
	},
	unsupportedText: {
		fontSize: 16,
		color: '#666',
		textAlign: 'center',
		lineHeight: 24,
	},
	// Styles for compound component pattern
	mobileHeader: {
		backgroundColor: '#fff',
		marginBottom: 8,
		marginHorizontal: 16,
		padding: 12,
		borderRadius: 8,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.1,
		shadowRadius: 2,
		elevation: 2,
	},
	mobileTitle: {
		fontSize: 16,
		fontWeight: '600',
		color: '#333',
		marginBottom: 8,
	},
	mobileTableRow: {
		flexDirection: 'row',
		backgroundColor: '#fff',
		marginBottom: 4,
		marginHorizontal: 16,
		padding: 8,
		borderRadius: 4,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 1,
		elevation: 1,
	},
	mobileTableCell: {
		flex: 1,
		paddingHorizontal: 4,
	},
	mobileTableCellText: {
		fontSize: 14,
		color: '#666',
	},
	numericText: {
		textAlign: 'right',
		fontFamily: 'monospace',
	},
	sortIndicator: {
		fontSize: 12,
		color: '#666',
	},
});

export { DataTable };
export { DataTableHeader };
export { DataTableTitle };
export { DataTableRow };
export { DataTableCell };
